import { useEffect, useState } from "react";
import { adminPipelineHealth } from "@/lib/api";
import { RefreshCw, CreditCard } from "lucide-react";

const LABELS = {
    stripe_payment_paid: "Stripe payment paid",
    stripe_webhook_received: "Stripe webhook received",
    purchase_receipt_email_sent: "Purchase-receipt email sent",
    entitlement_created: "Entitlement row created",
    user_tier_upgraded: "User tier upgraded",
};

function formatRelative(iso) {
    if (!iso) return "none in 24h";
    try {
        const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (secs < 60) return `${secs}s ago`;
        if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
        if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
        return `${Math.floor(secs / 86400)} d ago`;
    } catch { return iso; }
}

// Purchase pipeline health tile.
// Surfaces real-data telemetry from each downstream subsystem of the
// Stripe → webhook → Resend → entitlement → tier pipeline so the admin
// can see at a glance whether anything has broken in the last 24h
// WITHOUT needing to make a synthetic purchase.
export default function PipelineHealth() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try { setData(await adminPipelineHealth()); }
        catch { /* silent */ }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const checks = data?.checks || {};
    const checkValues = Object.values(checks);
    // "alive" when at least one subsystem had activity in the last 24h.
    // Use `some` rather than `every` so the global label is amber until
    // actual purchase traffic flows — and so an empty checks object
    // (loading) renders the neutral amber instead of false-positive green.
    const anyAlive = checkValues.length > 0 && checkValues.some((c) => c?.ok);

    return (
        <div className="bl-card p-5 mt-6" data-testid="admin-pipeline-health">
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-bl-muted" aria-hidden />
                    <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-semibold">
                            Purchase pipeline health · last 24h
                        </div>
                        <div className="text-[11px] text-bl-muted">
                            Real-data telemetry —{" "}
                            <span
                                className={
                                    anyAlive
                                        ? "text-[var(--bl-moss)] font-semibold"
                                        : "text-[var(--bl-amber)] font-semibold"
                                }
                            >
                                {anyAlive ? "ACTIVITY DETECTED" : "QUIET — no recent activity"}
                            </span>
                        </div>
                    </div>
                </div>
                <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    className="bl-btn-ghost p-1.5"
                    title="Refresh"
                    data-testid="admin-pipeline-refresh"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                </button>
            </div>
            <ul className="mt-3 space-y-1.5" data-testid="admin-pipeline-checks">
                {Object.entries(LABELS).map(([key, label]) => {
                    const c = checks[key] || {};
                    return (
                        <li
                            key={key}
                            className="flex items-center justify-between gap-2 text-sm"
                            data-testid={`admin-pipeline-${key}`}
                        >
                            <span className="flex items-center gap-2">
                                <span
                                    className={
                                        "inline-block w-2 h-2 rounded-full "
                                        + (c.ok ? "bg-[var(--bl-moss)]" : "bg-bl-muted opacity-50")
                                    }
                                    aria-hidden
                                />
                                <span className="text-bl">{label}</span>
                            </span>
                            <span className="text-[11px] text-bl-muted tnum">
                                {c.count_24h ?? 0} in 24h · last {formatRelative(c.last_at)}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
